import type { StrategyBlock } from '@segmently/schema-models/strategy';

export const STRATEGY_BLOCK_INSTRUCTION_LEVELS = [
  'mustHave',
  'should',
  'niceToHave',
  'forbidden',
] as const;

const INSTRUCTION_LEVEL_SET = new Set<string>(STRATEGY_BLOCK_INSTRUCTION_LEVELS);
const CUSTOM_SUBTYPE_SLUG = /^[a-z0-9][a-z0-9_-]*$/;

export class StrategyBlockWriteValidationError extends Error {
  readonly code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'StrategyBlockWriteValidationError';
    this.code = code;
  }
}

/**
 * Validate the additive B5 block metadata contract against the final stored
 * document. `undefined` models merge-write omission, not field deletion.
 */
export function validateStrategyBlockWrite(
  patch: Partial<StrategyBlock>,
  existing?: Partial<StrategyBlock>,
): void {
  const candidate = { ...existing } as Record<string, unknown>;
  for (const [key, value] of Object.entries(patch)) {
    if (value !== undefined) candidate[key] = value;
  }

  const customSubtype = candidate.customSubtype;
  if (customSubtype !== undefined) {
    if (typeof customSubtype !== 'string') {
      throw new StrategyBlockWriteValidationError(
        'invalid_custom_subtype',
        'customSubtype must be a string slug.',
      );
    }
    const slug = customSubtype.trim();
    if (!slug || slug !== customSubtype || !CUSTOM_SUBTYPE_SLUG.test(slug)) {
      throw new StrategyBlockWriteValidationError(
        'invalid_custom_subtype',
        'customSubtype must be a non-empty normalized slug that starts with a lowercase letter or digit and contains only lowercase letters, digits, underscores, or hyphens.',
      );
    }
    if (candidate.blockType !== 'custom') {
      throw new StrategyBlockWriteValidationError(
        'custom_subtype_requires_custom_block',
        'customSubtype can only be set when blockType is "custom".',
      );
    }
  }

  const instructions = candidate.instructions;
  if (instructions === undefined) return;
  if (!Array.isArray(instructions)) {
    throw new StrategyBlockWriteValidationError(
      'invalid_block_instructions',
      'instructions must be an array.',
    );
  }
  instructions.forEach((instruction, index) => {
    if (!instruction || typeof instruction !== 'object' || Array.isArray(instruction)) {
      throw new StrategyBlockWriteValidationError(
        'invalid_block_instruction',
        `instructions[${index}] must be an object.`,
      );
    }
    const entry = instruction as Record<string, unknown>;
    if (typeof entry.level !== 'string' || !INSTRUCTION_LEVEL_SET.has(entry.level)) {
      throw new StrategyBlockWriteValidationError(
        'invalid_block_instruction_level',
        `instructions[${index}].level must be one of ${STRATEGY_BLOCK_INSTRUCTION_LEVELS.join(', ')}.`,
      );
    }
    if (typeof entry.text !== 'string' || entry.text.trim().length === 0) {
      throw new StrategyBlockWriteValidationError(
        'invalid_block_instruction_text',
        `instructions[${index}].text must be a non-empty string.`,
      );
    }
    if (entry.id !== undefined && typeof entry.id !== 'string') {
      throw new StrategyBlockWriteValidationError(
        'invalid_block_instruction_id',
        `instructions[${index}].id must be a string when provided.`,
      );
    }
  });
}
